import { DataTableEmptyCell, DataTableRow } from "@bcc/components/data-table";
import type { PurchaseOrder, PurchaseOrderLine } from "@shared/types";
import { CellContext } from "@tanstack/react-table";
import { FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CarrierIcon } from "../carrier/carrier-icon";
import { CELL_LINK_CLASSES } from "../constants";

interface TrackingItem {
  trackingNumber: string;
  trackingUrl?: string;
  carrier?: string;
}

function deduplicateTracking(lineItems: PurchaseOrderLine[]): TrackingItem[] {
  const trackingMap = new Map<string, TrackingItem>();
  for (const lineItem of lineItems) {
    if (!lineItem.trackingNumber) continue;
    const trackingKey = `${lineItem.trackingNumber}|${lineItem.trackingUrl ?? ""}|${lineItem.carrier ?? ""}`;
    if (!trackingMap.has(trackingKey)) {
      trackingMap.set(trackingKey, {
        trackingNumber: lineItem.trackingNumber,
        trackingUrl: lineItem.trackingUrl,
        carrier: lineItem.carrier,
      });
    }
  }
  return Array.from(trackingMap.values());
}

export const OrderTrackingCell = (props: CellContext<DataTableRow<PurchaseOrder>, unknown>) => {
  const { t } = useTranslation();

  if (props.row.original.isLoading) {
    return (
      <span className="flex items-center gap-1.5 text-sm text-muted-foreground whitespace-nowrap">
        <FileText className="h-4 w-4" />
        {t("views.ordersTable.loadingShippingLabel")}
      </span>
    );
  }

  const { lineItems } = props.row.original;
  const uniqueTrackingItems = deduplicateTracking(lineItems);

  if (uniqueTrackingItems.length === 0) {
    return <DataTableEmptyCell />;
  }
  return (
    <ul className="list-none p-0 m-0 space-y-1 max-w-[160px]">
      {uniqueTrackingItems.map((tracking, trackingIndex) => (
        <li key={trackingIndex} className="flex items-center gap-1.5 text-sm min-w-0">
          <CarrierIcon carrier={tracking.carrier} />
          {tracking.trackingUrl ? (
            <a
              href={tracking.trackingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={`${CELL_LINK_CLASSES} block truncate`}
              onClick={(e) => e.stopPropagation()}
            >
              {tracking.trackingNumber}
            </a>
          ) : (
            <span className="font-mono block truncate">{tracking.trackingNumber}</span>
          )}
        </li>
      ))}
    </ul>
  );
};
